import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export function ProjectsSection() {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const images = [
    "https://static.readdy.ai/image/fdabe7c7ccee11942d3988daf4f91aee/3d07619ced27de30465e2f423aeb74c1.jpeg",
    "https://static.readdy.ai/image/fdabe7c7ccee11942d3988daf4f91aee/9664a2e968bfae2c89eb2e74ae8b6a28.jpeg",
    "https://static.readdy.ai/image/fdabe7c7ccee11942d3988daf4f91aee/2b01786dfccc0732593f85acdc21a95d.jpeg",
    "https://static.readdy.ai/image/fdabe7c7ccee11942d3988daf4f91aee/631293a7e9fdc6160d4713a2764d32f9.jpeg",
    "https://static.readdy.ai/image/fdabe7c7ccee11942d3988daf4f91aee/23d809614f1f919c30c3c613af2b4264.jpeg"
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section id="projects" className="py-16 sm:py-20 md:py-24">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Game Introduction */}
          <div className="space-y-6 sm:space-y-8 order-2 lg:order-1">
            <h3 className="text-3xl sm:text-4xl font-extralight text-white mb-6 sm:mb-8 tracking-[0.05em] px-2">
              {t('featuredGame')} <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">{t('game')}</span>
            </h3>
            
            <div className="space-y-4 sm:space-y-6 px-2">
              <h4 className="text-2xl sm:text-3xl font-light text-white tracking-wide">
                {t('oasisRichman')}
              </h4>
              
              <p className="text-white text-base sm:text-lg leading-relaxed font-light">
                {t('gameDescription')}
              </p>
              
              <div className="space-y-3 sm:space-y-4 pt-2 sm:pt-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-gamepad-line text-violet-400 text-lg sm:text-xl w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0"></i>
                  <div>
                    <h5 className="text-white font-medium mb-1 text-sm sm:text-base">{t('strategicGameplay')}</h5>
                    <p className="text-gray-300 text-xs sm:text-sm font-light">{t('strategicGameplayDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <i className="ri-team-line text-violet-400 text-lg sm:text-xl w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0"></i>
                  <div>
                    <h5 className="text-white font-medium mb-1 text-sm sm:text-base">{t('socialExperience')}</h5>
                    <p className="text-gray-300 text-xs sm:text-sm font-light">{t('socialExperienceDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <i className="ri-trophy-line text-violet-400 text-lg sm:text-xl w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0"></i>
                  <div>
                    <h5 className="text-white font-medium mb-1 text-sm sm:text-base">{t('richRewards')}</h5>
                    <p className="text-gray-300 text-xs sm:text-sm font-light">{t('richRewardsDesc')}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 sm:pt-6">
                <a 
                  href="https://play.google.com/store/apps/details?id=com.metayou.oasisrichman"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full sm:w-auto text-center group relative bg-transparent border border-gray-600 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-medium transition-all duration-300 hover:border-purple-400 hover:bg-purple-500/10 whitespace-nowrap cursor-pointer tracking-wide"
                >
                  {t('playNow')}
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Game Image Slideshow */}
          <div className="relative flex justify-center order-1 lg:order-2">
            <div className="w-full max-w-[320px] sm:max-w-[360px] md:max-w-[400px] aspect-[3/4] rounded-2xl sm:rounded-3xl overflow-hidden relative group">
              <div className="w-full h-full relative">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-700 ${
                      currentSlide === index ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img 
                      src={image}
                      alt={`${t('oasisRichman')} ${index + 1}`}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                ))}
              </div>
              
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 group-hover:from-black/40 transition-all duration-700"></div>

              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 via-purple-500/20 to-cyan-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-2xl sm:rounded-3xl"></div>

              {/* Slide indicators */}
              <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all duration-300 cursor-pointer ${
                      currentSlide === index 
                        ? 'bg-violet-400 w-6 sm:w-8' 
                        : 'bg-white/50 hover:bg-white/80'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}